import { useRecoilState } from "recoil";
import { businessState } from "../../State/Business";
import { useEffect, useCallback } from "react";
import { useApi } from "../useApi";
import { Result, isErr, ok } from "../../Utils/Result";
import { BusinessDTO, MemberRol } from "../../Types-DTOs/ClientUser";
import { ApiError } from "../useApi/Schemas";
import { useAuth } from "../useAuth";

/**
 * Only GET /business sends the credits and the Copilot subscription: the
 * business that other responses return keeps the ones already read.
 * @param previous - The business in the state.
 * @param next - The business a response returned.
 * @returns next, with what only GET /business sends taken from previous when next lacks it.
 */
export const keepGetBusinessExtras = (previous: BusinessDTO | undefined, next: BusinessDTO): BusinessDTO => ({
  ...next,
  credits: next.credits ?? previous?.credits,
  copilotSubscription:
    next.copilotSubscription !== undefined ? next.copilotSubscription : previous?.copilotSubscription,
});

// Module-level cache for simultaneous requests to avoid duplicate fetches
let globalBusinessPromise: Promise<any> | null = null;
let globalInvitesPromise: Record<string, Promise<any> | null> = {};

export const useBusiness = () => {
  const [business, setBusiness] = useRecoilState(businessState);
  const { isLoggedIn } = useAuth();
  const {
    getBusinessesEndpoint,
    inviteUserEndpoint,
    removeUserEndpoint,
    updateMemberRoleEndpoint,
    getPaymentUrlEndpoint,
    updateAllowedMemberQuantityEndpoint,
    getBillsEndpoint,
    getCancelSubscriptionUrlEndpoint,
    getCustomerPortalUrlEndpoint,
    getPaymentCustomerSessionEndpoint,
    getDetailedSubscriptionEndpoint,
    getInvitesEndpoint,
    deleteInvite
  } = useApi();

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    let isActive = true;

    const fetchInitialData = async () => {
      // Use the global promise if it exists to deduplicate simultaneous calls
      if (!globalBusinessPromise) {
        globalBusinessPromise = getBusinessesEndpoint();
      }

      const businessResponse = await globalBusinessPromise;

      // Clear the promise after it completes so future mounts can re-fetch if needed (e.g. after logout/login)
      // or if we want to force refresh. For now, we clear it here.
      globalBusinessPromise = null;

      if (!isActive) {
        return;
      }

      if (!isErr(businessResponse)) {
        const businessId = businessResponse.value.business.id;

        if (!globalInvitesPromise[businessId]) {
            globalInvitesPromise[businessId] = getInvitesEndpoint(businessId);
        }

        const invitesResponse = await globalInvitesPromise[businessId];
        globalInvitesPromise[businessId] = null;

        if (!isActive) {
          return;
        }
        if (!isErr(invitesResponse)) {
          setBusiness({
            business: businessResponse.value.business,
            invites: invitesResponse.value.invites
          })
        }
      }
    };

    fetchInitialData();

    return () => {
      isActive = false;
    };
  }, [isLoggedIn]);

  const inviteUser = async (props: {
    email: string;
    phone: string;
    name: string;
    rol: MemberRol;
  }): Promise<Result<BusinessDTO, ApiError>> => {
    const result = await inviteUserEndpoint(props);

    if (isErr(result)) {
      return result;
    }

    const businessResult = result.value.business;

    const invitesResult = await getInvitesEndpoint(businessResult.id);
    if (isErr(invitesResult)) {
      return invitesResult;
    }

    setBusiness((prevState) => ({
      business: keepGetBusinessExtras(prevState.business, businessResult),
      invites: invitesResult.value.invites
    }));

    return ok(businessResult);
  };

  const removeUser = async (memberId: string): Promise<Result<BusinessDTO, ApiError>> => {
    const result = await removeUserEndpoint(memberId);

    if (isErr(result)) {
      return result;
    }

    const businessResult = result.value.business;

    setBusiness((prevState) => ({
      ...prevState,
      business: keepGetBusinessExtras(prevState.business, businessResult)
    }));

    return ok(businessResult);
  };

  const updateMemberRole = async (memberId: string, rol: MemberRol): Promise<Result<BusinessDTO, ApiError>> => {
    const result = await updateMemberRoleEndpoint(memberId, rol);

    if (isErr(result)) {
      return result;
    }

    const businessResult = result.value.business;

    setBusiness((prevState) => ({
      ...prevState,
      business: keepGetBusinessExtras(prevState.business, businessResult)
    }));

    return ok(businessResult);
  };

  const getPaymentUrl = async (
    quantity: number
  ): Promise<Result<string, ApiError>> => {
    const result = await getPaymentUrlEndpoint(quantity);

    if (isErr(result)) {
      return result;
    }

    return ok(result.value.paymentUrl);
  };

  const getPaymentCustomerSession = async () => {
    const result = await getPaymentCustomerSessionEndpoint();

    if (isErr(result)) {
      return result;
    }

    return ok(result.value.customerSecret);
  };

  const updateAllowedMembers = async (allowedMembers: number) => {
    const result = await updateAllowedMemberQuantityEndpoint(allowedMembers);

    if (isErr(result)) {
      return result;
    }

    const businessResult = result.value.business;

    setBusiness((prevState) => ({
      ...prevState,
      business: keepGetBusinessExtras(prevState.business, businessResult)
    }));

    return ok(businessResult);
  };

  const getBills = async () => {
    const result = await getBillsEndpoint();

    if (isErr(result)) {
      return result;
    }

    return ok(result.value);
  };

  const getCancelSubscriptionUrl = async () => {
    const result = await getCancelSubscriptionUrlEndpoint();

    if (isErr(result)) {
      return result;
    }

    return ok(result.value.cancelSubscriptionUrl);
  };

  const getCustomerPortalUrl = async () => {
    const result = await getCustomerPortalUrlEndpoint();

    if (isErr(result)) {
      return result;
    }

    return ok(result.value.customerPortalUrl);
  };

  const resyncBusiness = async () => {
    const result = await getBusinessesEndpoint();

    if (isErr(result)) {
      return result;
    }

    const invitesResponse = await getInvitesEndpoint(result.value.business.id);
    if (!isErr(invitesResponse)) {
      setBusiness({
        business: result.value.business,
        invites: invitesResponse.value.invites
      });
    }

    return ok(result.value.business);
  };

  const getDetailedSubscription = async () => {
    const result = await getDetailedSubscriptionEndpoint();

    if (isErr(result)) {
      return result;
    }

    // Return the full response object which contains { subscription, trial }
    return ok(result.value);
  };

  const getInvites = useCallback(async () => {
    if (!business || !business.business) {
      return;
    }
    const businessId = business.business.id;
    const result = await getInvitesEndpoint(businessId);

    if (isErr(result)) {
      return result;
    }

    setBusiness((prevState) => ({
      ...prevState,
      invites: result.value.invites
    }));

    return ok(result.value);
  }, [business?.business?.id, getInvitesEndpoint, setBusiness]);

  const delInvite = async (inviteId: string) => {
    if (!business || !business.business) {
      return ok(false);
    }

    const businessId = business.business.id;
    const result = await deleteInvite(businessId, inviteId);
    if (isErr(result)) {
      return result;
    }

    const invitesResponse = await getInvitesEndpoint(businessId);
    if (!isErr(invitesResponse)) {
      setBusiness((prevState) => ({
        ...prevState,
        invites: invitesResponse.value.invites
      }));
    }

    return ok(true);
  }

  return {
    business: business.business,
    invites: business.invites,
    inviteUser,
    removeUser,
    updateMemberRole,
    getPaymentUrl,
    updateAllowedMembers,
    getBills,
    getCancelSubscriptionUrl,
    getCustomerPortalUrl,
    resyncBusiness,
    getPaymentCustomerSession,
    getDetailedSubscription,
    getInvites,
    delInvite
  };
};
